import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  getDownloadErrorMessage,
  saveBlobResponse,
} from '../../../application/utils/downloadFile';
import { OrderApiService } from '../../services/order-api/OrderApiService';
import {
  Order,
  OrderEventParticipant,
  OrderItem,
  OrderPaymentMethod,
  OrderStatus,
  OrderUser,
} from '../../types/Order';

interface PaymentForm {
  receivedByUserId: string;
  paymentMethod: OrderPaymentMethod;
  paymentReference: string;
}

@Component({
  selector: 'app-order-view-page',
  standalone: false,
  templateUrl: './order-view-page.component.html',
  styleUrl: './order-view-page.component.sass',
})
export class OrderViewPageComponent implements OnInit {
  public order: Order | null = null;
  public loading = false;
  public savingAction = false;
  public downloadingTickets = false;
  public downloadingTicketIds: Record<string, boolean> = {};
  public paymentFormVisible = false;
  public paymentForm: PaymentForm = this.createEmptyPaymentForm();
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderApi: OrderApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    const orderId = this.route.snapshot.paramMap.get('orderId');
    if (!orderId) {
      this.errorMessage = 'Заказ не найден.';
      return;
    }

    await this.loadOrder(orderId);
  }

  public get title(): string {
    return this.order?.code ?? 'Заказ';
  }

  public get purchaseTotal(): number {
    return this.order?.items.reduce((sum, item) => sum + (item.unitPurchasePrice ?? 0), 0) ?? 0;
  }

  public get markupTotal(): number {
    return (this.order?.total ?? 0) - this.purchaseTotal;
  }

  public get canSubmitPayment(): boolean {
    return !this.savingAction
      && Boolean(this.paymentForm.receivedByUserId)
      && Boolean(this.paymentForm.paymentMethod);
  }

  public get canDownloadTickets(): boolean {
    return this.order?.status === 'paid' && this.order.items.length > 0;
  }

  public openPaymentForm(): void {
    if (!this.order) {
      return;
    }

    this.paymentForm = {
      receivedByUserId: this.getInitialPaymentReceiverId(this.order),
      paymentMethod: this.order.paymentMethod ?? 'card',
      paymentReference: this.order.paymentReference ?? '',
    };
    this.paymentFormVisible = true;
  }

  public closePaymentForm(): void {
    this.paymentFormVisible = false;
  }

  public async submitPayment(): Promise<void> {
    if (!this.order || !this.canSubmitPayment) {
      this.errorMessage = 'Выберите ответственного за продажу и способ оплаты.';
      return;
    }

    this.savingAction = true;
    this.errorMessage = null;
    this.successMessage = null;
    try {
      const response = await this.orderApi.payOrder(this.order.id, {
        receivedByUserId: this.paymentForm.receivedByUserId,
        paymentMethod: this.paymentForm.paymentMethod,
        paymentReference: this.paymentForm.paymentReference.trim() || null,
      });
      const order = this.unwrapResponse(response);
      if (!order) {
        return;
      }

      this.order = order;
      this.paymentFormVisible = false;
      this.successMessage = `Заказ ${order.code} оплачен.`;
      this.notificationService.notify('order', 'Заказ оплачен', order.code);
      await this.router.navigate(['/dashboard/orders', order.code], { replaceUrl: true });
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingAction = false;
      this.changeDetector.detectChanges();
    }
  }

  public async cancelOrder(): Promise<void> {
    if (!this.order || !window.confirm(`Отменить ${this.order.code}? Билеты снова станут доступными.`)) {
      return;
    }

    await this.updateOrder();
  }

  public async downloadAllTickets(): Promise<void> {
    if (!this.order || !this.canDownloadTickets) {
      return;
    }

    this.downloadingTickets = true;
    this.errorMessage = null;
    this.successMessage = null;
    const errors: string[] = [];

    try {
      for (const item of this.order.items) {
        const errorMessage = await this.downloadOrderTicketItem(item);
        if (errorMessage) {
          errors.push(`${item.ticket?.code || item.ticketId}: ${errorMessage}`);
        }
      }

      if (errors.length > 0) {
        this.errorMessage = errors.join('\n');
      } else {
        this.successMessage = 'Билеты отправлены на скачивание.';
      }
    } finally {
      this.downloadingTickets = false;
      this.changeDetector.detectChanges();
    }
  }

  public async downloadTicket(item: OrderItem): Promise<void> {
    if (!this.order || !this.canDownloadTickets) {
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;
    const errorMessage = await this.downloadOrderTicketItem(item);
    if (errorMessage) {
      this.errorMessage = errorMessage;
    }
    this.changeDetector.detectChanges();
  }

  public isTicketDownloading(item: OrderItem): boolean {
    return this.downloadingTickets || Boolean(this.downloadingTicketIds[item.ticketId]);
  }

  public getStatusName(status: OrderStatus): string {
    const names: Record<OrderStatus, string> = {
      cancelled: 'Отменен',
      draft: 'Черновик',
      expired: 'Резерв истек',
      paid: 'Оплачен',
      refunded: 'Возврат',
      reserved: 'Резерв',
    };
    return names[status];
  }

  public getStatusClass(status: OrderStatus): string {
    if (status === 'paid') {
      return 'paid';
    }
    if (status === 'reserved') {
      return 'reserved';
    }
    if (status === 'cancelled' || status === 'expired') {
      return 'cancelled';
    }

    return 'muted';
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatOptionalMoney(value: number | null): string {
    return value === null ? '—' : this.formatMoney(value);
  }

  public formatPaymentMethod(method: OrderPaymentMethod | null): string {
    const names: Record<OrderPaymentMethod, string> = {
      bank_account: 'Расчетный счет',
      card: 'Карта',
    };
    return method ? names[method] : '—';
  }

  public formatUser(user: OrderUser | null): string {
    return user?.displayName || user?.username || '—';
  }

  public getParticipantName(participant: OrderEventParticipant): string {
    return participant.displayName || participant.username;
  }

  public getItemMarkup(unitSalePrice: number, unitPurchasePrice: number | null): number | null {
    return unitPurchasePrice === null ? null : unitSalePrice - unitPurchasePrice;
  }

  public formatEventDate(value: string | null): string {
    if (!value) {
      return '—';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(value));
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return '—';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackByParticipant(_index: number, participant: OrderEventParticipant): string {
    return participant.userId;
  }

  private async loadOrder(orderId: string): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.orderApi.getOrder(orderId);
      const order = this.unwrapResponse(response);
      if (!order) {
        this.errorMessage = 'Заказ не найден.';
        return;
      }

      this.order = order;
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async updateOrder(): Promise<void> {
    if (!this.order) {
      return;
    }

    this.savingAction = true;
    this.errorMessage = null;
    this.successMessage = null;
    try {
      const response = await this.orderApi.cancelOrder(this.order.id);
      const order = this.unwrapResponse(response);
      if (!order) {
        return;
      }

      const actionName = 'отменен';
      this.order = order;
      this.successMessage = `Заказ ${order.code} ${actionName}.`;
      this.notificationService.notify('order', `Заказ ${actionName}`, order.code);
      await this.router.navigate(['/dashboard/orders', order.code], { replaceUrl: true });
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingAction = false;
      this.changeDetector.detectChanges();
    }
  }

  private async downloadOrderTicketItem(item: OrderItem): Promise<string | null> {
    if (!this.order) {
      return 'Заказ не найден.';
    }

    this.downloadingTicketIds = {
      ...this.downloadingTicketIds,
      [item.ticketId]: true,
    };

    try {
      const response = await this.orderApi.downloadTicketFile(this.order.id, item.ticketId);
      const ticketCode = item.ticket?.code || item.ticketId;
      saveBlobResponse(response, `${this.order.code}-${ticketCode}.pdf`);
      return null;
    } catch (error) {
      return getDownloadErrorMessage(error, `Не удалось скачать билет ${item.ticket?.code || item.ticketId}.`);
    } finally {
      this.downloadingTicketIds = {
        ...this.downloadingTicketIds,
        [item.ticketId]: false,
      };
    }
  }

  private createEmptyPaymentForm(): PaymentForm {
    return {
      receivedByUserId: '',
      paymentMethod: 'card',
      paymentReference: '',
    };
  }

  private getInitialPaymentReceiverId(order: Order): string {
    if (order.receivedByUserId && order.event?.participants.some((participant) => participant.userId === order.receivedByUserId)) {
      return order.receivedByUserId;
    }

    return order.event?.participants[0]?.userId ?? '';
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
